import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useTranslation } from "react-i18next";

// Purpose-built "Filtres" dialog shared by Produits/Ingrédients/Options/
// Utilisateurs/Commandes. CustomDialog (src/components/cutomPopUp.jsx) is a
// confirm/cancel dialog and doesn't comfortably fit a close icon + a
// Réinitialiser/Appliquer footer, so this is a standalone component built to
// the same MUI Dialog conventions. `children` are the FilterChipGroup
// sections (Catégorie, Remise, Trier par, ...).
//
// Everything inside operates on "draft" state owned by the calling screen:
// closing via the X icon or the backdrop (`onClose`) discards in-progress
// changes, `onReset` resets both the draft AND the applied filters to their
// defaults right away (and closes the dialog) since "no filters" is always a
// safe, unambiguous action, and `onApply` commits the draft into the
// screen's applied filters. `disableApply` greys out Appliquer while the
// draft filters exactly match the applied ones (nothing pending to commit).
const FiltersDialog = ({
  open,
  onClose,
  onReset,
  onApply,
  title,
  children,
  disableApply = false,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {title || t("filters.filtersTitle")}
        <IconButton
          aria-label={t("common.clear")}
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}
      >
        {children}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" color="inherit" sx={{ flex: 1 }} onClick={onReset}>
          {t("filters.reset")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ flex: 1 }}
          onClick={onApply}
          disabled={disableApply}
        >
          {t("filters.apply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiltersDialog;

// Small reusable trigger button for opening a FiltersDialog, kept here so
// every screen wires it up the same way instead of hand-rolling a Button.
export const FiltersButton = ({ onClick, activeCount = 0 }) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outlined"
      color="inherit"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      startIcon={<FilterListIcon />}
      sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
    >
      {t("filters.filtersTitle")}
      {activeCount > 0 ? ` (${activeCount})` : ""}
    </Button>
  );
};
